/**
 * SMS Handoff Trigger Prompt
 * Evaluates if a conversation should trigger a handoff and extracts structured fields
 * for downstream workflow selection. Does NOT select the workflow itself.
 */
import { z } from 'zod';
import { ROUTING_DEPARTMENTS_SCOPE } from './routing-departments-scope.js';
/**
 * Schema for handoff trigger evaluation + extraction response
 * Determines if trigger conditions are met and extracts structured fields
 */
export const SMSHandoffTriggerSchema = z.object({
    // Trigger decision
    shouldHandoff: z
        .boolean()
        .describe('Whether the conversation should trigger a handoff based on trigger conditions'),
    triggerConditionsMet: z
        .array(z.string())
        .describe('Array of trigger condition descriptions that were met (empty if none)'),
    reasoning: z
        .string()
        .describe('Clear explanation of the decision, including which trigger conditions were met'),
    // Extraction fields (for workflow selection if shouldHandoff=true)
    transferIndividuals: z
        .array(z.string())
        .default([])
        .describe('Names of specific people the AI assistant confirmed it would connect or hand off to (e.g., "John Smith", "the manager")'),
    transferDepartments: z
        .array(z.string())
        .default([])
        .describe(`Only departments or teams the assistant confirmed for handoff. ${ROUTING_DEPARTMENTS_SCOPE}`),
    conversationTopics: z
        .array(z.string())
        .default([])
        .describe('Main topics discussed in the conversation (e.g., "rent payment", "maintenance request", "lease renewal")'),
    escalationReason: z
        .string()
        .default('')
        .describe('Brief description of why escalation is needed (e.g., "User frustrated", "Requires human approval")'),
    contactGroup: z
        .string()
        .default('unknown')
        .describe('Contact group classification based on conversation context: "owner", "tenant", "vendor", "resident", or "unknown"'),
    requiresFollowUp: z
        .boolean()
        .default(true)
        .describe('Whether the discussed topics require follow-up action by the team'),
});
/**
 * Prompt template for SMS handoff trigger evaluation
 * Evaluates trigger conditions and extracts structured fields for workflow selection
 */
export const smsHandoffTriggerPrompt = {
    name: 'sms-handoff-trigger',
    version: '1.6.0',
    systemPrompt: `<conversation_evaluator_prompt>
<role>You are a <b>conversation evaluator</b> for our SMS triage system. Your task is to:
1. Determine whether a conversation should trigger a handoff to a human
2. Extract structured information from the conversation for routing
</role>

<agent_context_usage>
The user prompt includes this agent's knowledge base content and custom instructions (if any). Use them to decide whether the assistant could still answer or triage from that material versus when a human is required. If no knowledge base is attached, infer capability only from the conversation and custom instructions.
</agent_context_usage>

<trigger_handoff_conditions>
<description>Always trigger a handoff if any of these conditions are met</description>
<rules>
<rule>Human assistance, feedback, or review is needed</rule>
<rule>The situation requires a human contacting or following up with the customer as a next step</rule>
<rule>The agent's next step is to submit a request, log a message, or similar notification</rule>
<rule>The agent can no longer assist, answer, or triage from its knowledge</rule>
<rule>The user expresses irritation, frustration, extreme urgency. Examples include: use of swear words, "This is bull", "I need to talk to someone", "Get back to me right now" or similar dissatisfied phrases.</rule>
<rule>The issue needs human action next (for example requires vendor coordination, approvals, or follow up information)</rule>
<rule>Emergency criteria has been met (e.g., fire, flooding, gas, safety issue)</rule>
<rule>Conversation has naturally completed and the user confirms no further assistance is needed (e.g. "that's it, thanks")</rule>
</rules>
</trigger_handoff_conditions>

<do_not_trigger_handoff_conditions>
<rule>If the request is vague or incomplete, gather more information and continue assisting, unless trigger_handoff_conditions have been met</rule>
<rule>If the AI can assist through answering FAQs.</rule>
<rule>Do not escalate if AI has asked for input and is still waiting for a response. The exception is if an escalation, action, or contact is required as a next step, then always immediately trigger handoff</rule>
</do_not_trigger_handoff_conditions>

<extraction_requirements>
<description>Always extract the following information from the conversation, regardless of handoff decision</description>
<routing_departments_definition>${ROUTING_DEPARTMENTS_SCOPE}</routing_departments_definition>
<fields>
<field name="transferIndividuals">ONLY names of specific people the AI assistant explicitly confirmed it would connect the user to, notify, or hand off to (e.g. "I'll have our property manager reach out", "I'm connecting you with John"). If the assistant is only gathering information, answering FAQs, or has not committed to a specific person, use an empty array. Do NOT include people the user asked for if the assistant declined, could not commit, or did not confirm that handoff.</field>
<field name="transferDepartments">ONLY departments or teams the assistant explicitly confirmed it would route, notify, or hand off to, using the routing_departments_definition above. If the assistant is only taking a note or has not committed, use an empty array. Do NOT include targets only mentioned by the user or in passing if the assistant did not confirm that transfer or routing.</field>
<field name="conversationTopics">Main topics discussed. Examples: "rent payment issue", "AC not working", "lease renewal question". Include emergency-related topics explicitly when applicable (e.g., "gas leak", "fire", "flooding").</field>
<field name="escalationReason">Brief reason for escalation if shouldHandoff=true. Examples: "User frustrated with response time", "Requires vendor coordination"</field>
<field name="contactGroup">Classify the contact as "owner", "tenant", "vendor", "resident", or "unknown" based on context clues in the conversation</field>
<field name="requiresFollowUp">Set to true if the conversation topics need follow-up action by the team (default true for handoffs)</field>
</fields>
</extraction_requirements>

<final_decision_logic>
<rule type="always">
Trigger a handoff (shouldHandoff=true) when ANY trigger_handoff_conditions are met. When judging whether the AI can still assist, use the provided agent knowledge base and custom instructions together with the conversation.
</rule>
<rule type="never">Do not prematurely handoff if the user has not responded yet, UNLESS a trigger condition is met</rule>
<rule type="never">Do not handoff if the AI can still assist from its knowledge and instructions, UNLESS a trigger condition is met</rule>
</final_decision_logic>

<output_requirements>
You must provide structured JSON output with these exact fields:
- shouldHandoff: boolean (true if trigger conditions met, false otherwise)
- triggerConditionsMet: string array listing which specific trigger conditions were met
- reasoning: string explaining your decision
- transferIndividuals: string array of people the assistant confirmed for handoff/routing (empty if none)
- transferDepartments: string array of confirmed departments per routing_departments_definition (empty if none)
- conversationTopics: string array of main topics
- escalationReason: string explaining why handoff is needed (if shouldHandoff=true)
- contactGroup: string classification of the contact type ("owner", "tenant", "vendor", "resident", or "unknown")
- requiresFollowUp: boolean indicating if follow-up action is needed
</output_requirements>
</conversation_evaluator_prompt>`,
    userPrompt: `AGENT KNOWLEDGE BASE (content the SMS assistant may use):
{{knowledgeBasesContext}}

AGENT CUSTOM INSTRUCTIONS (if empty, the company did not set extra instructions):
{{agentPromptContext}}

CONVERSATION HISTORY:
{{conversationHistory}}

TASK: Evaluate whether this conversation should trigger a handoff based on the trigger conditions. Also extract structured information (transfer targets the assistant confirmed, topics) from the conversation for routing purposes.`,
    variables: [
        'conversationHistory',
        'knowledgeBasesContext',
        'agentPromptContext',
    ],
    metadata: {
        description: 'Evaluates SMS conversations for handoff trigger and extracts structured routing fields',
        model: 'gpt-5-2025-08-07',
        temperature: 0.1,
        maxTokens: 900,
        category: 'evaluation',
        channel: 'SMS',
    },
};
//# sourceMappingURL=sms-handoff-trigger.js.map